import {
  AppSyncFarm,
  AppSyncFarmToken,
  MonitorVaults,
} from 'common/models/farm';
import React from 'react';
import { AppSyncUserInfo } from 'common/models/userInfo';
import { FarmUtils } from 'utils/farmUtils';
import {
  EventInputChangeArgs,
  EventUpdateAccountInfoArgs,
  initialDepositContext,
  vaultDepositService,
} from 'xstate/vaultDepositMachine';
import LiquidityInput from '../GeneralInput/LiquidityInput';
import { CryptoUtils } from 'utils/cryptoUtils';
import { NumberUtils } from 'utils/numberUtils';
import SpinnerButton from '../SpinnerButton';
import { VaultDepositEvent } from 'xstate/vaultDepositEvent';
import { AdminAction, AdminButton } from '../AdminButton';
import SelectVaultToken from '../SelectToken/SelectVaultToken';
import { RemoteEventTxSuccess, RemoteTxEvent } from 'xstate/txMachine';
import { XStateUtils } from 'xstate/xStateUtils';
import { ChainId, ETH_VAULT_WRAPPER } from 'constants/constants';
import {
  addLiquidityModalService,
  initialVaultModalContext,
} from 'xstate/vaultModalMachine';
import { VaultModalEvent } from 'xstate/vaultModalEvent';
import DepositCompleteModalContainer from './DepositCompleteModalContainer';
import { OptionalOutboundLink } from '../OptionalOutboundLink';
import Switch from '../Switch';
import { debounce } from 'lodash';
import SwitchNetworkContainer from 'containers/SwitchNetworkContainer';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { Nullable } from 'common/models/optional';
import { VaultApi } from 'sdk/vaultApi';
import { chains } from 'containers/WalletProvider/WalletProvider';
import { MetricsButton } from '../LinkButton/MetricsButton';

type Props = {
  depositToken: AppSyncFarmToken | undefined;
  pairedToken: Nullable<AppSyncFarmToken | undefined>;
  farms: AppSyncFarm[];
  userInfoLoading: boolean;
  userInfo: AppSyncUserInfo;
  claiming: boolean;
  monitorVaults: MonitorVaults[] | [];
  chainId: ChainId;
  handleDepositComplete: () => void;
  handleModalClose: () => void;
  refetchFarmUserInfo: () => Promise<void>;
  updatePool: (poolId: number) => Promise<void>;
  accountAddress?: string;
};

const Deposit: React.FC<Props> = (props) => {
  const [depositToken, setDepositToken] = React.useState(props.depositToken);
  const { chainId } = useSelectedChain();
  const chain = chains.find((c) => c.id === chainId);
  const { address: accountAddress } = useAccount();
  const { openConnectModal } = useConnectModal();

  const [amount, setAmount] = React.useState(initialDepositContext.amount);
  const [tokenBalance, setTokenBalance] = React.useState(0n);
  // const [tokenBalanceUsd, setTokenBalanceUsd] = React.useState(initialDepositContext.availableTokenUsd);
  const [tokenMaxDeposit, setTokenMaxDeposit] = React.useState(
    initialDepositContext.tokenMaxDeposit
  );
  const [tokenAllowance, setTokenAllowance] = React.useState(
    initialDepositContext.tokenAllowance
  );
  const [inputError, setInputError] = React.useState(
    initialDepositContext.inputError
  );
  const [dataError, setDataError] = React.useState(
    initialDepositContext.dataError
  );
  const [isLoadingData, setIsLoadingData] = React.useState(
    initialDepositContext.isLoadingData
  );
  const [isDepositing, setIsDepositing] = React.useState(
    initialDepositContext.isDepositing
  );
  const [isApprovingToken, setIsApprovingToken] = React.useState(
    initialDepositContext.isApprovingToken
  );
  const [approveExactAmount, setApproveExactAmount] = React.useState(
    initialDepositContext.approveExactAmount
  );
  const [storedTx, setStoredTx] = React.useState(
    initialDepositContext.storedTx
  );
  const [selectedToken, setSelectedToken] = React.useState(
    initialDepositContext.token
  );

  const isTokenEth =
    selectedToken === 'eth' &&
    depositToken?.displayName.toLowerCase() === 'weth' &&
    chainId !== ChainId.Polygon &&
    chainId !== ChainId.Celo;
  const depositTokenDisplayName = isTokenEth
    ? 'ETH'
    : (depositToken?.displayName === 'ETHC' ? 'ETH' : depositToken?.displayName);

  const [isDepositModalOpen, setIsDepositModalOpen] = React.useState(
    initialVaultModalContext.isDepositModalOpen
  );
  React.useEffect(() => {
    const subscription = addLiquidityModalService.subscribe((state) => {
      // if (state.context.isDepositModalOpen !== isDepositModalOpen) {
      //   setIsDepositModalOpen(() => state.context.isDepositModalOpen);
      // }
      // if (state.context.isWithdrawModalOpen !== isWithdrawModalOpen) {
      //   setIsWithdrawModalOpen(() => state.context.isWithdrawModalOpen);
      // }
    });

    return subscription.unsubscribe;
    // Note that if isDepositModalOpen, isWithdrawModalOpen are not included in the array here
    // Then the function will not re-render
  }, [addLiquidityModalService, isDepositModalOpen]);

  const openDepositModal = () => {
    addLiquidityModalService.send(VaultModalEvent.OPEN_DEPOSIT_MODAL);
  };

  const selectableTokens = props.farms
    .map((f) => FarmUtils.getBaseToken(f))
    .sort((a, b) => {
      return a.displayName > b.displayName ? 1 : -1;
    })
    .filter((t, index, array) => {
      return array.map((t1) => t1.address.toLowerCase()).indexOf(t.address.toLowerCase()) === index;
    });

  let pairableTokens = props.farms
    .slice()
    .filter((f) => FarmUtils.getBaseToken(f).address.toLowerCase() === depositToken?.address.toLowerCase())
    .map((f) => FarmUtils.getRewardToken(f))
    .sort((a, b) => {
      return a.displayName > b.displayName ? 1 : -1;
    });

  const pairedTokenByDepositToken =
    pairableTokens.length === 1 ? pairableTokens[0] : undefined;
  const [pairedToken, setPairedToken] = React.useState<
    AppSyncFarmToken | undefined | null
  >(props.pairedToken ? props.pairedToken : pairedTokenByDepositToken);

  const possibleVault = props.farms
    .slice()
    .filter(
      (f) => FarmUtils.getBaseToken(f).address === depositToken?.address
    )[0];

  const selectedVault = pairedToken
    ? FarmUtils.getVaultByTokens(
        props.farms,
        depositToken?.address.toLowerCase() ?? '',
        pairedToken.address.toLowerCase()
      )
    : undefined;

  let selectedVaultApr = selectedVault
    ? FarmUtils.getFeeAprByVaultAddress(
        props.monitorVaults,
        selectedVault.lpAddress
      ) ?? 0
    : 0;
    selectedVaultApr = selectedVaultApr < 0.01 ? 0 : selectedVaultApr;
  const strSelectedVaultApr =
  selectedVaultApr === 0 ? '--' : selectedVaultApr.toFixed(2).toString();

  const bgClass = 'content-inner-bg-color';
  const labelClass = '';

  const refreshData = async () => {
    if (selectedVault && chainId) {
      await props.updatePool(selectedVault.poolId);
      await props.refetchFarmUserInfo();
    }
  };
  const debouncedRefreshData = React.useMemo(
    () => debounce(refreshData, 750),
    [chainId, selectedVault]
  );

  React.useEffect(() => {
    const subscription = vaultDepositService.subscribe((state) => {
      // if (state.context.tokenPrice !== price) {
      //   setPrice(state.context.tokenPrice);
      // }

      if (state.context.availableToken !== tokenBalance) {
        setTokenBalance(state.context.availableToken);
      }

      // if (state.context.availableTokenUsd !== tokenBalanceUsd) {
      //   setTokenBalanceUsd(state.context.availableTokenUsd);
      // }

      if (state.context.tokenMaxDeposit !== tokenMaxDeposit) {
        setTokenMaxDeposit(state.context.tokenMaxDeposit ?? 0n);
      }

      if (state.context.tokenAllowance !== tokenAllowance) {
        setTokenAllowance(state.context.tokenAllowance ?? 0n);
      }

      if (state.context.inputError != inputError) {
        setInputError(state.context.inputError);
      }

      if (state.context.dataError != dataError) {
        setDataError(state.context.dataError);
      }

      if (state.context.isLoadingData !== isLoadingData) {
        setIsLoadingData(state.context.isLoadingData);
      }

      if (state.context.isDepositing !== isDepositing) {
        setIsDepositing(state.context.isDepositing);
      }

      if (state.context.isApprovingToken !== isApprovingToken) {
        setIsApprovingToken(state.context.isApprovingToken);
      }

      // if (state.context.approveExactAmount !== approveExactAmount) {
      //   setApproveExactAmount(state.context.approveExactAmount);
      // }

      if (state.context.storedTx !== storedTx) {
        setStoredTx(state.context.storedTx);
      }
    });

    vaultDepositService.onEvent((e) => {
      if (e.type === RemoteTxEvent.REMOTE_TX_SUCCESS) {
        const successEventValue = (e as RemoteEventTxSuccess).value
          .functionName;
        if (successEventValue && successEventValue === 'vaultDeposit') {
          setAmount('0');
          openDepositModal();
          props.handleDepositComplete();
        }
        void debouncedRefreshData();
      }
    });

    return subscription.unsubscribe;
  }, [
    vaultDepositService,
    amount,
    // price,
    storedTx,
    inputError,
    dataError,
    tokenBalance,
    // tokenBalanceUsd,
    tokenMaxDeposit,
    isLoadingData,
    isDepositing,
    isApprovingToken,
    tokenAllowance,
    // approveExactAmount
    // isPercentageSelectorDisabled
  ]);

  React.useEffect(() => {
    const args: EventInputChangeArgs = {
      amount: amount,
      approveExactAmount: approveExactAmount,
    };
    vaultDepositService.send(
      VaultDepositEvent.INPUT_CHANGE,
      XStateUtils.createEventPayload(args)
    );
  }, [amount, approveExactAmount]);

  React.useEffect(() => {
    if (
      VaultApi.Vault.getInstance() != null &&
      chainId &&
      (selectedVault ?? possibleVault)
    ) {
      const payload: EventUpdateAccountInfoArgs = {
        address: accountAddress || null,
        chainId: chainId,
        farm: selectedVault ?? possibleVault,
        token: selectedToken,
      };
      vaultDepositService.send(
        VaultDepositEvent.UPDATE_ACCOUNT_INFO,
        XStateUtils.createEventPayload(payload)
      );
    }
  }, [chainId, accountAddress, selectedVault, possibleVault, selectedToken]);

  const onDepositTokenChange = (token: AppSyncFarmToken) => {
    setDepositToken(token);
    setAmount('0');
    setApproveExactAmount(false);
    pairableTokens = props.farms
      .slice()
      .filter((f) => FarmUtils.getBaseToken(f).address === token.address)
      .map((f) => FarmUtils.getRewardToken(f))
      .sort((a, b) => {
        return a.displayName > b.displayName ? 1 : -1;
      });
    if (pairableTokens.length === 1) {
      setPairedToken(pairableTokens[0]);
    } else {
      setPairedToken(null);
    }
  };

  const isDepositButtonDisabled =
    !amount ||
    amount === '0' ||
    isDepositing ||
    inputError != null ||
    dataError != null ||
    isLoadingData ||
    selectedVault === undefined;

  const adminActions: AdminAction[] = [
    {
      text: 'Reset Token Allowance',
      onClick: () =>
        vaultDepositService.send(VaultDepositEvent.RESET_TOKEN_ALLOWANCE),
    },
  ];

  const handleModalClose = () => {
    addLiquidityModalService.send(VaultModalEvent.CLOSE_DEPOSIT_MODAL);
    props.handleModalClose();
  };

  const isEthVault = selectedVault && ETH_VAULT_WRAPPER[selectedVault.lpAddress.toLocaleLowerCase()];

  const toggleToken = () => {
    setAmount('0');
    if (isTokenEth) {
      setSelectedToken('weth');
    } else {
      setSelectedToken('eth');
    }
  };

  const showAllowToken =
    !isTokenEth &&
    (NumberUtils.stringToBN(amount, BigInt(depositToken?.decimals ?? 18)) >
      tokenAllowance ||
      tokenAllowance === 0n);

  return (
    <>
      <div className="flex flex-col max-w-2xl mx-auto">
        <div className="grid">
          <div className="flex flex-row justify-between items-center tablet:ml-5 mb-5">
            <div className="text-left primary-text-color font-semibold text-xl">
              Select Tokens
            </div>
            {selectedVault && <MetricsButton address={selectedVault.lpAddress} className="tablet:mr-5" />}
          </div>
          <div
            className={`flex flex-col ${bgClass} rounded-[20px] pb-7 pt-5 px-5 tablet:px-7`}
          >
            <div className="flex flex-row gap-5 justify-between">
              <SelectVaultToken
                name="deposit-token"
                values={selectableTokens}
                isLoading={isLoadingData}
                value={depositToken}
                onChange={onDepositTokenChange}
                id="oneToken"
                label="Deposit Token"
                labelClass={labelClass}
              />
              <SelectVaultToken
                name="paired-token"
                values={pairableTokens}
                isLoading={isLoadingData}
                value={pairedToken}
                onChange={setPairedToken}
                id="pairedToken"
                label="Paired Token"
                labelClass={labelClass}
                isClearable
              />
            </div>
            {isEthVault && (
              <div className="my-3">
                <button
                  onClick={toggleToken}
                  className={'vault-button primary-bg-color text-white'}
                >
                  Deposit {isTokenEth ? 'wETH' : 'ETH'}
                </button>
              </div>
            )}
            <div
              className={
                'bg-white text-black mt-5 flex flex-col tablet:flex-row justify-between text-lg font-medium py-3 px-5 rounded-[10px]'
              }
            >
              <span>7-day fee APR</span>
              <span className="primary-color font-bold">
                {strSelectedVaultApr}%
              </span>
            </div>
            {selectedVault && (
              <div className={`mt-5 underline ${labelClass}`}>
                <OptionalOutboundLink
                  href={
                    CryptoUtils.getScannerInfo(
                      selectedVault.chainId,
                      selectedVault?.lpAddress ?? '',
                      'address'
                    )?.link
                  }
                >
                  View on {CryptoUtils.getScanName(selectedVault.chainId)}
                </OptionalOutboundLink>
              </div>
            )}
          </div>
          {selectedVault && dataError != null && !isLoadingData && (
            <button
              className="primary-button-bg-color text-white font-semibold mt-5 py-[10px] px-10 rounded-[10px]"
              type="button"
              disabled={false}
              onClick={() =>
                vaultDepositService.send(VaultDepositEvent.LOAD_DATA)
              }
              data-dd-action-name={`Load Data for ${selectedVault.displayName}`}
              title="Load Vault"
            >
              Load Vault
            </button>
          )}

          <div className="text-left primary-text-color m-5 mt-10 font-semibold text-xl">
            Deposit Amounts
          </div>
          {inputError != null && <div className="error">{inputError}</div>}
          <LiquidityInput
            token={depositToken}
            isNumber={true}
            label="Enter Amount"
            step="1"
            value={amount}
            onChange={setAmount}
            placeholder="Enter Amount"
            isDisabled={selectedVault === undefined || isLoadingData}
            tokenBalance={tokenBalance}
            selectedToken={selectedToken}
            chainId={chainId}
            isLoading={isLoadingData}
          />
          {selectedVault &&
            dataError === null &&
            tokenMaxDeposit.toString() !== CryptoUtils.MAX_NUMBER && (
              <div className="mt-3 warning-text-color text-sm font-semibold w-full text-center">
                {`Max ${depositTokenDisplayName} Deposit
                ${NumberUtils.BNtoNumberWithoutDecimals(
                  tokenMaxDeposit.toString(),
                  depositToken?.decimals || 18
                )}`}
              </div>
            )}
        </div>
        <div className="flex justify-center gap-3 items-center max-w-2xl mt-5">
          {props.accountAddress ? (
            <>
              {selectedVault && chainId != selectedVault.chainId ? (
                <SwitchNetworkContainer
                  btnClassName="loadable-default button primary-button-bg-color primary-button-color w-full h-16 font-semibold  rounded-[10px]"
                  newChainId={selectedVault.chainId}
                />
              ) : (
                <>
                  {showAllowToken ? (
                    <div className="flex flex-col items-center w-full gap-5">
                      <SpinnerButton
                        className="deposit-button h-16 w-full rounded-[10px]"
                        isLoading={isApprovingToken || isLoadingData}
                        disabled={isDepositButtonDisabled}
                        onClick={() =>
                          vaultDepositService.send(
                            VaultDepositEvent.APPROVE_TOKEN_ALLOWANCE
                          )
                        }
                        data-dd-action-name={`Approve ${depositTokenDisplayName}`}
                      >
                        Allow ICHI Protocol to use your $
                        {depositTokenDisplayName}
                      </SpinnerButton>
                      <Switch
                        isChecked={approveExactAmount}
                        labelText="Allow exact amount"
                        labelPosition="left"
                        switchColor="#1877F6"
                        switchSize="60px"
                        onChange={() =>
                          setApproveExactAmount(!approveExactAmount)
                        }
                        className="flex justify-center items-center gap-4"
                        isMaterial={true}
                        isDisabled={
                          selectedVault === undefined || isLoadingData
                        }
                      />
                    </div>
                  ) : (
                    <SpinnerButton
                      className="deposit-button h-16 w-full rounded-[10px]"
                      isLoading={isDepositing || isLoadingData}
                      disabled={isDepositButtonDisabled}
                      onClick={() =>
                        vaultDepositService.send(VaultDepositEvent.DEPOSIT)
                      }
                      data-dd-action-name={`Deposit ${depositTokenDisplayName}`}
                    >
                      Deposit
                    </SpinnerButton>
                  )}
                </>
              )}
            </>
          ) : (
            <SpinnerButton
              className="deposit-button h-16 w-full rounded-[10px]"
              isLoading={false}
              disabled={false}
              onClick={openConnectModal}
              data-dd-action-name="Connect Wallet"
            >
              Connect Wallet
            </SpinnerButton>
          )}

          {chain?.testnet && <AdminButton actions={adminActions} />}
        </div>
      </div>
      <DepositCompleteModalContainer
        isShowing={isDepositModalOpen}
        depositTokenDisplayName={depositTokenDisplayName ?? ''}
        vaultDisplayName={selectedVault?.displayName ?? ''}
        depositedValue={storedTx?.value}
        vaultIrr={strSelectedVaultApr}
        toggle={handleModalClose}
      />
    </>
  );
};

export default Deposit;
